import { useEffect, useMemo } from "react";
import { message } from "antd";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { useSportsbookAnalyticsSummary } from "../../../queries/sportsbook/useSportsbookAnalyticsSummary";
import type { ReportingSummaryFiltersValue } from "../../../queries/sportsbook/reportingSummaryFilterOptions";
import { getProfitabilitySummaryColumns } from "../profitabilityReportingColumns";
import ProfitabilityTableSection from "./ProfitabilityTableSection";
import {
  useProfitabilityFilterOptions,
  useProfitabilitySectionFilterState,
} from "./useProfitabilitySectionFilters";

type Props = {
  date: string;
  inheritedFilters: ReportingSummaryFiltersValue;
};

function ProfitabilitySummarySection({ date, inheritedFilters }: Props) {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const { filters, setFilters, setBetType, setMoneyType } =
    useProfitabilitySectionFilterState(inheritedFilters);
  const query = useSportsbookAnalyticsSummary({
    start_date: date,
    end_date: date,
    page: 1,
    size: 1,
    ...filters,
  });
  const options = useProfitabilityFilterOptions(
    query.filters,
    setFilters,
    date,
  );
  const columns = useMemo(
    () => getProfitabilitySummaryColumns(currencySymbol),
    [currencySymbol],
  );

  useEffect(() => {
    if (query.isError) {
      message.error(AppStrings.REPORTING_SUMMARY_FETCH_ERROR);
    }
  }, [query.isError]);

  return (
    <ProfitabilityTableSection
      id="reporting-profitability-summary"
      title={AppStrings.SPORTSBOOK_PROFITABILITY_SUMMARY}
      rows={query.rows}
      columns={columns}
      loading={query.isLoading || query.isFetching}
      betType={filters.bet_type}
      moneyType={filters.money_type}
      betTypeOptions={options?.bet_type}
      moneyTypeOptions={options?.money_type}
      onBetTypeChange={setBetType}
      onMoneyTypeChange={setMoneyType}
    />
  );
}

export default ProfitabilitySummarySection;
